import { gql, NetworkStatus, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useApolloClient, useMutation, useReadQuery } from '@apollo/client/react'
import { ConfirmDialog, ErrorAlert } from '@repo/shared-client/components/blocks'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AspectRatio,
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Spinner,
} from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import SFMediaImage from '@web/src/components/primatives/sf-media-image/sf-media-image'
import SFPageLoading from '@web/src/components/primatives/sf-page-loading/sf-page-loading'
import { usePullToRefresh } from '@web/src/contexts/pull-to-refresh-context'
import MediaAdminEditDialog from '@web/src/features/media/media-admin-edit/media-admin-edit-dialog'
import MediaAdminEditMenuItems from '@web/src/features/media/media-admin-edit/media-admin-edit-menu-items'
import MediaUploaderDialog from '@web/src/features/media/media-uploader-dialog/media-uploader-dialog'
import { useAuth } from '@web/src/hooks/use-auth'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { EllipsisIcon, ImageIcon, SaveIcon } from 'lucide-react'
import React, { useRef, useState } from 'react'
import toast from 'react-hot-toast'
import type {
  DeleteAttachedMediaMutation,
  DeleteAttachedMediaMutationVariables,
  GroupCoverImageQuery,
  GroupCoverImageQueryVariables,
  SetGroupCoverImageMutation,
  SetGroupCoverImageMutationVariables,
} from 'types/graphql'

const GROUP_COVER_IMAGE_QUERY: TypedDocumentNode<GroupCoverImageQuery, GroupCoverImageQueryVariables> = gql`
  query GroupCoverImageQuery($groupIdOrSlug: String!) {
    group: group(groupIdOrSlug: $groupIdOrSlug) {
      id
      slug
      name
      canEdit
      coverImage
      coverNsfwRating
      coverImageUrlMedium
      coverImageOffsetX
      coverImageOffsetY
      attachedMedia {
        fileName
        height
        id
        imageUrlMedium
        mediaType
        nsfwRating
        ownerId
        width
      }
    }
  }
`

const SET_GROUP_COVER_IMAGE: TypedDocumentNode<SetGroupCoverImageMutation, SetGroupCoverImageMutationVariables> = gql`
  mutation SetGroupCoverImageMutation($groupIdOrSlug: String!, $input: SetGroupCoverImageInput!) {
    setGroupCoverImage(groupIdOrSlug: $groupIdOrSlug, input: $input) {
      id
      slug
      coverImage
      coverImageUrlMedium
      coverNsfwRating
      coverImageOffsetX
      coverImageOffsetY
    }
  }
`

const DELETE_IMAGE: TypedDocumentNode<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables> = gql`
  mutation DeleteAttachedMedia($id: String!) {
    deleteAttachedMedia(id: $id) {
      id
    }
  }
`

type AttachedMediaItem = NonNullable<GroupCoverImageQuery['group']>['attachedMedia'][number]

interface ImageCardProps {
  media: AttachedMediaItem
  idx: number
  onImageSelected: (idx: number) => void
  onRefetch: () => void
  isSelected: boolean
  isCurrent: boolean
}

const ImageCard = ({ media, idx, onImageSelected, onRefetch, isSelected, isCurrent }: ImageCardProps) => {
  const { t } = useTranslation()
  const [deleteModal, setDeleteModal] = useState(false)
  const [adminEditModal, setAdminEditModal] = useState(false)

  const [deleteImage] = useMutation<DeleteAttachedMediaMutation, DeleteAttachedMediaMutationVariables>(DELETE_IMAGE, {
    onCompleted: () => {
      toast.success(t('Image Deleted'))
      onRefetch()
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const handleDeleteImage = () => {
    setDeleteModal(false)
    deleteImage({ variables: { id: media.id } })
  }

  const handleDeleteClick = () => {
    setDeleteModal(true)
  }

  const cardClass = cn(
    'cursor-pointer overflow-hidden rounded-xl border-2 transition-colors',
    isSelected && 'border-primary ring-2 ring-primary/30',
    isCurrent && !isSelected && 'border-muted-foreground',
    !(isCurrent || isSelected) && 'border border-muted-foreground/10'
  )

  return (
    <div className="relative">
      <AspectRatio className="relative w-[110px] min-w-[110px]" ratio={1}>
        <Card className={cn('h-full w-full p-0', cardClass)} onClick={() => onImageSelected(idx)}>
          <SFMediaImage className="h-full w-full" disableNSFWBlur={true} media={media} />
          {isCurrent && !isSelected && (
            <Badge className="absolute top-1 left-1" size="sm">
              {t('Current')}
            </Badge>
          )}
        </Card>
      </AspectRatio>

      <div className="absolute top-1 right-1 z-10">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button aria-label={t('Menu')} className="rounded-full ring-1 ring-black" size="icon" type="button" />
            }
          >
            <EllipsisIcon />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <MediaAdminEditMenuItems mediaId={media.id} onOpen={() => setAdminEditModal(true)} showSeparator={false} />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleDeleteClick} variant="destructive">
              {t('Delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmDialog
        onConfirm={handleDeleteImage}
        onOpenChange={(e) => setDeleteModal(e.open)}
        open={deleteModal}
        text={t('Are you sure you want to delete this image?')}
        title={t('Are you sure?')}
        variant="delete"
      />
      <MediaAdminEditDialog
        isAttached
        mediaId={media.id}
        onMediaChange={onRefetch}
        onOpenChange={(e) => setAdminEditModal(e.open)}
        open={adminEditModal}
      />
    </div>
  )
}

export const Route = createFileRoute('/(auth)/(main-layout)/group/$groupIdOrSlug_/cover-image')({
  component: GroupCoverImagePage,
  head: () =>
    seo({
      title: `Set Cover Image | ${env.VITE_APP_NAME}`,
      description: 'Set group cover image',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
    }),
  loader: async ({ context, params }) => {
    const { queryRef } = await queryAndPreload(context, GROUP_COVER_IMAGE_QUERY, {
      variables: { groupIdOrSlug: params.groupIdOrSlug },
    })
    return { queryRef }
  },
})

function GroupCoverImagePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apolloClient = useApolloClient()
  const { queryRef } = Route.useLoaderData()
  const { data, error, networkStatus } = useReadQuery(queryRef as QueryRef<GroupCoverImageQuery>)

  // All hooks must be called before any early returns
  const { hasPermission } = useAuth()
  const { setDisabled } = usePullToRefresh()
  const [selectedMedia, setSelectedMedia] = useState<AttachedMediaItem | null>(null)
  const [offsetPercent, setOffsetPercent] = useState(50)
  const [uploadImageModal, setUploadImageModal] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartY, setDragStartY] = useState(0)
  const [initialOffsetPercent, setInitialOffsetPercent] = useState(50)

  // Track initial state for change detection
  const [initialCoverImageId, setInitialCoverImageId] = useState<string | null>(null)
  const [initialOffsetY, setInitialOffsetY] = useState(50)

  const previewRef = useRef<HTMLDivElement>(null)

  const [setCoverImage, { loading }] = useMutation<SetGroupCoverImageMutation, SetGroupCoverImageMutationVariables>(
    SET_GROUP_COVER_IMAGE,
    {
      onCompleted: () => {
        toast.success(t('Cover image updated successfully'))
        const groupSlug = data?.group?.slug
        if (groupSlug) {
          navigate({ to: '/group/$groupIdOrSlug', params: { groupIdOrSlug: groupSlug } } as never)
        }
      },
      onError: (mutationError) => {
        toast.error(mutationError.message)
      },
    }
  )

  const refetchQuery = React.useCallback(async () => {
    await apolloClient.refetchQueries({
      include: [GROUP_COVER_IMAGE_QUERY],
    })
  }, [apolloClient])

  const group = data?.group ?? null

  // Find current cover image media and set initial state
  React.useEffect(() => {
    if (group?.coverImage && group?.attachedMedia) {
      const currentCoverMedia = group.attachedMedia.find((media) => media.fileName === group.coverImage)
      if (currentCoverMedia) {
        setSelectedMedia(currentCoverMedia)
        setInitialCoverImageId(currentCoverMedia.id)
        const currentOffsetY = group.coverImageOffsetY || 50
        setOffsetPercent(currentOffsetY)
        setInitialOffsetY(currentOffsetY)
      }
    } else {
      // No cover image set initially
      setInitialCoverImageId(null)
      setInitialOffsetY(50)
    }
  }, [group?.coverImage, group?.attachedMedia, group?.coverImageOffsetY])

  // Disable pull-to-refresh when dragging
  React.useEffect(() => {
    setDisabled(isDragging)
  }, [isDragging, setDisabled])

  // Check permissions
  const canEdit = Boolean(group?.canEdit || hasPermission('group:update:all'))

  // Check if there are changes to enable/disable save button
  const hasChanges = React.useMemo(() => {
    if (!selectedMedia) return false

    // Check if a different image is selected
    const imageChanged = selectedMedia.id !== initialCoverImageId

    // Check if position changed (only relevant if same image is selected)
    const positionChanged = selectedMedia.id === initialCoverImageId && offsetPercent !== initialOffsetY

    return imageChanged || positionChanged
  }, [selectedMedia, initialCoverImageId, offsetPercent, initialOffsetY])

  const handleCancel = () => {
    navigate({ to: '/group/$groupIdOrSlug', params: { groupIdOrSlug: group.slug } } as never)
  }

  const handleImageSelected = (idx: number) => {
    const media = group.attachedMedia[idx]
    if (!media) return
    setSelectedMedia(media)

    // If this is the current cover image, use the stored offset, otherwise default to 50%
    if (media.fileName === group.coverImage) {
      setOffsetPercent(group.coverImageOffsetY || 50)
    } else {
      setOffsetPercent(50)
    }
  }

  const handleSave = () => {
    if (!selectedMedia) {
      toast.error(t('Please select an image'))
      return
    }

    if (!hasChanges) {
      toast.error(t('No changes to save'))
      return
    }

    setCoverImage({
      variables: {
        groupIdOrSlug: group.slug,
        input: {
          attachedMediaId: selectedMedia.id,
          offsetX: 0,
          offsetY: offsetPercent,
        },
      },
    })
  }

  // Helper function to get Y coordinate from mouse or touch event
  const getClientY = (e) => {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientY
    }
    if (e.changedTouches && e.changedTouches.length > 0) {
      return e.changedTouches[0].clientY
    }
    return e.clientY
  }

  // Mouse event handlers
  const handleMouseDown = (e) => {
    setIsDragging(true)
    setDragStartY(getClientY(e))
    setInitialOffsetPercent(offsetPercent)
    e.preventDefault()
  }

  // Touch event handlers
  const handleTouchStart = (e) => {
    setIsDragging(true)
    setDragStartY(getClientY(e))
    setInitialOffsetPercent(offsetPercent)
    e.preventDefault()
  }

  const handleMove = React.useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!(isDragging && previewRef.current && selectedMedia)) return

      const currentY = getClientY(e)
      const deltaY = currentY - dragStartY
      const containerHeight = previewRef.current.offsetHeight

      // Calculate the actual displayed image height based on cover background-size behavior
      const imageAspectRatio = selectedMedia.width / selectedMedia.height
      const containerAspectRatio = previewRef.current.offsetWidth / containerHeight

      let displayedImageHeight: number
      if (imageAspectRatio > containerAspectRatio) {
        displayedImageHeight = containerHeight
      } else {
        displayedImageHeight = previewRef.current.offsetWidth / imageAspectRatio
      }

      const maxOffset = Math.max(0, displayedImageHeight - containerHeight)

      if (maxOffset === 0) {
        setOffsetPercent(50)
        return
      }

      // Convert delta pixels to percentage change - INVERT deltaY for natural scrolling
      const percentChange = (-deltaY / maxOffset) * 100
      const newOffsetPercent = Math.round(Math.max(0, Math.min(100, initialOffsetPercent + percentChange)))

      setOffsetPercent(newOffsetPercent)
    },
    [isDragging, dragStartY, initialOffsetPercent, selectedMedia]
  )

  const handleEnd = React.useCallback(() => {
    setIsDragging(false)
  }, [])

  // Add event listeners for both mouse and touch events
  React.useEffect(() => {
    if (isDragging) {
      // Mouse events
      const handleMouseMove = (e) => handleMove(e)
      const handleMouseUp = () => handleEnd()

      // Touch events
      const handleTouchMove = (e) => {
        e.preventDefault() // Prevent scrolling while dragging
        handleMove(e)
      }
      const handleTouchEnd = () => handleEnd()

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleTouchEnd)
      document.addEventListener('touchcancel', handleTouchEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
        document.removeEventListener('touchcancel', handleTouchEnd)
      }
    }
  }, [isDragging, handleMove, handleEnd])

  const previewMedia = selectedMedia
  const previewOffsetPercent = selectedMedia ? offsetPercent : group.coverImageOffsetY || 50

  const isInitialLoading = networkStatus === NetworkStatus.loading
  if (isInitialLoading) return <SFPageLoading />
  if (error) return <ErrorAlert error={error} />
  if (!group) return <ErrorAlert error={new Error(t('Group not found'))} />

  if (!canEdit) {
    return (
      <div className="mx-auto w-full max-w-2xl px-4">
        <Alert variant="destructive">
          <AlertTitle>{t('Access Denied')}</AlertTitle>
          <AlertDescription>{t("You don't have permission to set the cover image for this group.")}</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-2 pt-4">
      <div className="flex w-full flex-col gap-4">
        <SFBackPageHeader lowerText={group.name} onBackClick={handleCancel} title={t('Edit Group Cover Image')} />

        <div className="flex flex-col gap-2">
          <p className="font-medium">{selectedMedia ? t('Adjust Cover Image Position') : t('Current Cover Image')}</p>
          <AspectRatio className="max-h-[200px] w-full" ratio={2.35}>
            {/* biome-ignore lint/a11y/noStaticElementInteractions: draggable cover crop preview */}
            {/* biome-ignore lint/a11y/noNoninteractiveElementInteractions: mouse/touch drag handlers */}
            <div
              className="h-full w-full overflow-hidden rounded-xl border bg-card ring-1 ring-foreground/10"
              onMouseDown={selectedMedia ? handleMouseDown : undefined}
              onTouchStart={selectedMedia ? handleTouchStart : undefined}
              ref={previewRef}
              style={{
                cursor: isDragging ? 'grabbing' : selectedMedia ? 'grab' : 'default',
                touchAction: selectedMedia ? 'none' : 'auto',
              }}
            >
              {previewMedia ? (
                <SFMediaImage
                  backgroundPosition={`center ${previewOffsetPercent}%`}
                  backgroundSize="cover"
                  className="h-full w-full"
                  disableNSFWBlur={true}
                  media={previewMedia}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  {t('No cover image set')}
                </div>
              )}
            </div>
          </AspectRatio>
          {selectedMedia ? (
            <p className="text-center text-muted-foreground">
              {t('Click and drag or touch and drag the image above to position it vertically')}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-2">
          <p className="font-medium">{t('Available Images')}</p>
          <div className="flex w-full flex-wrap items-start justify-evenly gap-3">
            <AspectRatio className="w-[110px] min-w-[110px]" ratio={1}>
              <Card
                className="flex h-full w-full cursor-pointer flex-col items-center justify-center border-dashed p-0"
                onClick={() => setUploadImageModal(true)}
              >
                <CardContent className="flex flex-col items-center justify-center gap-1 p-2">
                  <ImageIcon className="size-8" />
                  <span className="text-center text-base">
                    {t('Upload')}
                    <br />
                    {t('Images')}
                  </span>
                </CardContent>
              </Card>
            </AspectRatio>

            {group.attachedMedia?.map((media, idx) => (
              <ImageCard
                idx={idx}
                isCurrent={media.fileName === group.coverImage}
                isSelected={selectedMedia?.id === media.id}
                key={media.id}
                media={media}
                onImageSelected={handleImageSelected}
                onRefetch={() => refetchQuery()}
              />
            ))}
          </div>
        </div>

        <div className="flex w-full justify-end gap-2 border-border border-t pt-4 pb-10">
          <Button onClick={handleCancel} type="button" variant="outline">
            {t('Cancel')}
          </Button>
          <Button disabled={!(selectedMedia && hasChanges)} onClick={handleSave} type="button">
            {loading ? <Spinner className="size-4" /> : <SaveIcon />}
            {t('Save Cover Image')}
          </Button>
        </div>
      </div>

      <MediaUploaderDialog
        disableVideo={true}
        groupId={group.id}
        onOpenChange={(e) => setUploadImageModal(e.open)}
        onUploadComplete={() => {
          refetchQuery()
          setUploadImageModal(false)
        }}
        open={uploadImageModal}
      />
    </div>
  )
}
